import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { noop } from '~/_shared/utils/function.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import { useSelector } from '~/_shared/utils/hooks/useSelector';
import { createSpreadsheetMetricModelForDataActions } from '~/_shared/utils/metric/defaultMetric.helpers';
import { useDefaultMetrics } from '~/_shared/utils/metric/defaultMetric.hook';
import {
  mapMetricsToServerModel, type MetricModel,
} from '~/_shared/utils/metric/metrics.factory';
import { areMetricsTheSame } from '~/_shared/utils/metric/metrics.helpers';
import { MetricsDataAction } from '~/_shared/utils/metric/metrics.types';
import { type ModalProps } from '~/modal/modalType.enum';
import {
  getBoundariesGroupIdByType, getTargetNumberOfTerritories,
} from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/createBoundaryGroupFromAIModal.helpers';
import { useGetColumnsConstraints } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/useGetColumnsConstraints';
import type { CreateOptimizedTerritoriesConfig } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createOptimizedTerritoriesModal/createOptimizedTerritories.types';
import { CreateOptimizedTerritoriesModalComponent } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createOptimizedTerritoriesModal/createOptimizedTerritoriesModal.component';
import { useCreateOptimizedTerritories } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createOptimizedTerritoriesModal/useCreateOptimizedTerritories';
import {
  boundaryGroupsSelector, useCustomBoundaryGroupsSelector, useGetBoundaryGroup, useGlobalBoundaryGroups,
} from '~/store/boundaryGroups/boundaryGroups.selectors';
import { boundaryCreateOptimizedAddItem } from '~/store/frontendState/mapTools/boundary/boundaryCreateOptimized/boundaryCreateOptimized.actionCreators';
import { INITIAL_BOUNDARY_CREATE_OPTIMIZED_STATUS } from '~/store/frontendState/mapTools/boundary/boundaryCreateOptimized/boundaryCreateOptimized.state';
import {
  boundarySelectOptimizedTerritoriesSubsetOpenModal, boundarySelectSetSelectType,
} from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.actionCreators';
import { BoundarySelectType } from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.state';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { usePrimarySpreadsheetId } from '~/store/selectors/usePrimarySpreadsheetId';
import { usePrimarySpreadsheetFilterTreeItem } from '~/store/spreadsheetData/filtering/usePrimarySpreadsheetFilterTreeItemRequest';
import {
  BoundaryGroupFromAIModalSteps, BuildingBlockSelections, BuildingBlockSubsetTypes, NumberOfTerritoriesLastUpdateReason,
} from '../createBoundaryGroupFromAIModal/createBoundaryGroupFromAIModal.types';

const createEmptyConfiguration: () => CreateOptimizedTerritoriesConfig = () => ({
  iterations: 2000,
  blockSubset: null,
  aggMetrics: [],
  globalMetrics: [{
    id: 'topological',
    name: 'Topological',
    weight: 1,
    minWeight: OPTIMIZED_TERRITORY_TOPOLOGICAL_WEIGHT_MIN,
    maxWeight: OPTIMIZED_TERRITORY_TOPOLOGICAL_WEIGHT_MAX,
  }],
  previousTerritoriesBoundaryGroupId: null,
  constraints: [],
  btgSettings: null,
  anchorMap: null,
  targetTerritoryCount: null,
  apImportance: null,
  excludeDisconnectedBlocks: false,
  exportConfig: null,
  boundaryGroupId: null,
  selectedBoundaryIds: [],

  applyFilterOnData: false,
  buildingBlockSelection: BuildingBlockSelections.ALL,
  buildingBlockSubsetType: BuildingBlockSubsetTypes.ON_MAP_BY_LASSO,
  dataMetricsValues: [],
  demographicsValues: [],
  groupName: '',
  numberOfPreviousTerritories: null,
  numberOfSalesReps: null,
  numberOfTerritories: null,
  optimizationConstraints: false,
  overrideTerritory: false,
  previouslySavedBoundaryGroupId: null,
  respectPrevTerritories: false,
  respectSalesLocations: false,
  salesRepLocationsMap: null,
  salesRepNameColumnId: null,
  useNumberOfTerritories: false,
});

export type CreateOptimizedTerritoriesModalProps = ModalProps<{
  initialConfig?: CreateOptimizedTerritoriesConfig;
  initialStep?: BoundaryGroupFromAIModalSteps;
}>;

export const OPTIMIZED_TERRITORY_WEIGHT_MIN = 1;
export const OPTIMIZED_TERRITORY_WEIGHT_MAX = 100;

export const OPTIMIZED_TERRITORY_TOPOLOGICAL_WEIGHT_MIN = 1;
export const OPTIMIZED_TERRITORY_TOPOLOGICAL_WEIGHT_MAX = 20;

export const CreateOptimizedTerritoriesModalContainer = ({
  initialConfig,
  initialStep,
  isOpen,
  onClose,
}: CreateOptimizedTerritoriesModalProps) => {
  const [t] = useTranslation();
  const clientId = useClientIdSelector();
  const dispatch = useDispatch();
  const mapId = useMapIdSelector();
  const [currentStep, setCurrentStep] = useState<BoundaryGroupFromAIModalSteps>(initialStep ?? BoundaryGroupFromAIModalSteps.BuildingBlock);
  const [config, setConfig] = useState<CreateOptimizedTerritoriesConfig>({ ...createEmptyConfiguration(), ...initialConfig });
  const [optimizedTerritoriesRequestError, setOptimizedTerritoriesRequestError] = useState<string | null>(null);
  const { createOptimizedTerritoriesRequest } = useCreateOptimizedTerritories();
  const spreadsheetId = usePrimarySpreadsheetId();
  const boundariesGroups = useSelector(boundaryGroupsSelector);
  const { getBoundaryGroupById } = useGetBoundaryGroup();
  const { columnsConstraints, isLoading: isLoadingColumnsConstraints } = useGetColumnsConstraints(spreadsheetId);
  const filter = usePrimarySpreadsheetFilterTreeItem();
  const boundariesGroupsZipAndStatesIds = useMemo(() => getBoundariesGroupIdByType(boundariesGroups), [boundariesGroups]);
  const customBoundaryGroups = useCustomBoundaryGroupsSelector();
  const { computeDefaultMetrics } = useDefaultMetrics();
  const [isGeneratingOptimizedTerritories, setIsGeneratingOptimizedTerritories] = useState(false);
  const [isLoadingSubsetBlocks] = useState(false);
  const globalBoundaryGroups = useGlobalBoundaryGroups();

  const onGoToStep = useCallback((step: BoundaryGroupFromAIModalSteps) => {
    setCurrentStep(step);
  }, []);

  useEffect(() => {
    setConfig(previous => ({
      ...previous,
      dataMetricsValues: Object.entries(columnsConstraints).map(([columnId, { name, min, max, numericPercent }]) => ({
        id: columnId,
        name,
        checked: false,
        weight: OPTIMIZED_TERRITORY_WEIGHT_MAX,
        isConstraint: false,
        min,
        max,
        numericPercent,
      })),
    }));
  }, [columnsConstraints]);

  useEffect(() => {
    if (!config.boundaryGroupId) {
      return;
    }

    const boundaryGroup = getBoundaryGroupById(config.boundaryGroupId);

    setConfig(previous => ({
      ...previous,
      demographicsValues: (boundaryGroup?.demographics ?? []).map(demographic => ({
        id: demographic.id,
        name: demographic.name,
        weight: OPTIMIZED_TERRITORY_WEIGHT_MAX,
        min: OPTIMIZED_TERRITORY_WEIGHT_MIN,
        max: OPTIMIZED_TERRITORY_WEIGHT_MAX,
        checked: false,
        isConstraint: false,
      })),
    }));
  }, [boundariesGroups, boundariesGroupsZipAndStatesIds.statesGroupId, boundariesGroupsZipAndStatesIds.zipsGroupId, config.boundaryGroupId, getBoundaryGroupById, t]);

  const onSelectSubsetFromMap = useCallback(() => {
    if (!config.boundaryGroupId) {
      return;
    }

    onClose();
    dispatch(boundarySelectSetSelectType(BoundarySelectType.Lasso));

    dispatch(boundarySelectOptimizedTerritoriesSubsetOpenModal({
      boundaryGroupId: config.boundaryGroupId,
      selectedBoundaryIds: config.selectedBoundaryIds,
      configuration: config,
    }));
  }, [config, dispatch, onClose]);

  const onChange = useCallback((update: Partial<CreateOptimizedTerritoriesConfig>) => {
    setOptimizedTerritoriesRequestError(null);
    setConfig(previous => ({
      ...previous,
      ...update,
    }));

  }, []);

  const onCloseOptimizedTerritoriesRequestError = useCallback(() => {
    setOptimizedTerritoriesRequestError(null);
  }, []);

  const onSubmit = useCallback(async () => {
    if (clientId && mapId && spreadsheetId && config.boundaryGroupId) {
      setOptimizedTerritoriesRequestError(null);
      setIsGeneratingOptimizedTerritories(true);
      try {
        const { defaultSpreadsheetColumnMetrics, defaultDemographicsMetrics } = await computeDefaultMetrics();

        const creationParametersMetrics: MetricModel[] = [];

        config.dataMetricsValues.forEach((metric) => {
          if (!metric.checked) {
            return;
          }
          creationParametersMetrics.push(...createSpreadsheetMetricModelForDataActions(
            metric.id,
            spreadsheetId,
            metric.name,
            [MetricsDataAction.HIGH_VALUE, MetricsDataAction.LOW_VALUE, MetricsDataAction.SUM, MetricsDataAction.AVERAGE]
          ));
        });

        const filteredDefaultMetric = [...defaultSpreadsheetColumnMetrics, ...defaultDemographicsMetrics].filter((defaultMetric) => {
          return !creationParametersMetrics.some((creationMetric) => areMetricsTheSame(defaultMetric, creationMetric));
        });

        const combinedMetrics = [...creationParametersMetrics, ...filteredDefaultMetric];

        const serverModelMetrics = mapMetricsToServerModel(combinedMetrics);

        const response = await createOptimizedTerritoriesRequest({
          clientId,
          mapId,
          spreadsheetId,
          ...config,
          blockSubset: config.blockSubset ?? undefined,
          boundaryGroupId: config.boundaryGroupId,
          targetTerritoryCount: getTargetNumberOfTerritories(config) ?? undefined,
          btgSettings: {
            metrics: serverModelMetrics,
          },
          previousTerritoriesBoundaryGroupId: config.previousTerritoriesBoundaryGroupId ?? undefined,
          apImportance: config.apImportance ?? undefined,
          exportConfig: config.exportConfig ?? undefined,
        });

        if (response.hash) {
          dispatch(boundaryCreateOptimizedAddItem({
            hash: response.hash,
            status: INITIAL_BOUNDARY_CREATE_OPTIMIZED_STATUS,
            startedOnCurrentTab: true,
          }));
        }

        onClose();
      }
      catch (e) {
        setOptimizedTerritoriesRequestError(e.message);
      }
      finally {
        setIsGeneratingOptimizedTerritories(false);
      }
    }
  }, [clientId, mapId, spreadsheetId, config, createOptimizedTerritoriesRequest, dispatch, onClose, computeDefaultMetrics]);

  return (
    <CreateOptimizedTerritoriesModalComponent
      config={config}
      currentStep={currentStep}
      customBoundaryGroups={customBoundaryGroups}
      globalBoundaryGroups={globalBoundaryGroups}
      isGenerateOptimizedTerritoriesLoading={isGeneratingOptimizedTerritories}
      isLoadingDataMetrics={isLoadingColumnsConstraints}
      isLoadingSubsetBlocks={isLoadingSubsetBlocks}
      isOpen={isOpen}
      numberOfTerritoriesLoading={false} // TODO: remove??
      numberOfTerritoriesUpdateReason={NumberOfTerritoriesLastUpdateReason.Other} // TODO: remove??
      onChange={onChange}
      onClose={onClose}
      onCloseOptimizedTerritoriesRequestError={onCloseOptimizedTerritoriesRequestError}
      onGoToStep={onGoToStep}
      onRefreshTerritoriesCount={noop} // TODO: remove??
      onSelectSubsetFromMap={onSelectSubsetFromMap}
      onSubmit={onSubmit}
      optimizedTerritoriesRequestError={optimizedTerritoriesRequestError}
      markerFilterActive={!!filter}
    />
  );
};
